import mt4Logo from 'assets/images/icons/mt4_logo.png';
import mt5Logo from 'assets/images/icons/mt5_logo.png';
import cTraderLogo from 'assets/images/icons/cTrader.png';
import cTraderLogoDark from 'assets/images/icons/cTrader-dark.png';
import dxtradeLogo from 'assets/images/icons/dxtrade_icon.png';
import dxtradeLogoDark from 'assets/images/icons/dxtrade_icon-dark.png';
import tradelockerLogo from 'assets/images/icons/tradelocker.png';
import tradelockerLogoDark from 'assets/images/icons/tradelocker-dark.png';
import matchTraderLogo from 'assets/images/icons/matchTrader.png';
import matchTraderLogoDark from 'assets/images/icons/matchTrader-dark.png';
import tradovateLogo from 'assets/images/icons/tv.png';
import tradovateLogoDark from 'assets/images/icons/tv-dark.png';
import projectXLogo from 'assets/images/icons/projectx.png';
import cTraderNameless from 'assets/images/icons/ctrader_nameless.webp';
import dxtradeNameless from 'assets/images/icons/dxtrade_nameless.webp';
import matchTraderNameless from 'assets/images/icons/matchtrader_nameless.webp';
import projectXNameless from 'assets/images/icons/projectx_nameless.webp';
import rithmicLogo from 'assets/images/icons/rithmic.webp';
import rithmicNameless from 'assets/images/icons/rithmic_nameless.webp';
import tradovateNameless from 'assets/images/icons/tv_nameless.webp';
import tradelockerNameless from 'assets/images/icons/tradelocker_nameless.svg';
import ninjaTraderLogo from 'assets/images/icons/nt.webp';
import ninjaTraderNameless from 'assets/images/icons/nt_nameless.png';

/**
 * Every platform code the accounts table can hold, exactly as stored and exactly as
 * `connector-lib/const.go` declares them. This is the wire vocabulary: gRPC replies,
 * PostHog events (client and server) and MySQL all speak it, so nothing translates.
 */
export const PLATFORM_API_CODES = ['MT-4', 'MT-5', 'CTR', 'DXT', 'TL', 'MTR', 'TV', 'NT', 'PROJECT-X', 'RM'] as const;

export type PlatformApiCode = (typeof PLATFORM_API_CODES)[number];

/**
 * Single source of truth for trading-platform metadata.
 *
 * `apiCode` is the raw value returned by the account-manager API; `displayName`
 * is the human-readable label. `logoDark` falls back to `logo` for platforms
 * that ship only one asset.
 */
export interface PlatformMeta {
  apiCode: PlatformApiCode;
  displayName: string;
  /** AddAccountDrawer's picker id, when it differs from apiCode and displayName. */
  drawerId?: string;
  logo: string;
  logoDark: string;
  /** Icon-only mark (no wordmark) — used where the name is rendered as text alongside, e.g. filter options. */
  namelessLogo?: string;
  /** Light-mode badge background for marks whose strokes vanish on a white surface (e.g. PROJECT-X). */
  iconBg?: string;
  /** Dark-mode badge background; omit to render the bare mark, which already shows on the dark surface. */
  iconBgDark?: string;
}

export const PLATFORMS: PlatformMeta[] = [
  { apiCode: 'MT-4', displayName: 'MetaTrader 4', drawerId: 'MT4', logo: mt4Logo, logoDark: mt4Logo },
  { apiCode: 'MT-5', displayName: 'MetaTrader 5', drawerId: 'MT5', logo: mt5Logo, logoDark: mt5Logo },
  { apiCode: 'CTR', displayName: 'cTrader', logo: cTraderLogo, logoDark: cTraderLogoDark, namelessLogo: cTraderNameless },
  {
    apiCode: 'DXT',
    displayName: 'DXtrade',
    drawerId: 'DXTrade',
    logo: dxtradeLogo,
    logoDark: dxtradeLogoDark,
    namelessLogo: dxtradeNameless
  },
  { apiCode: 'TL', displayName: 'TradeLocker', logo: tradelockerLogo, logoDark: tradelockerLogoDark, namelessLogo: tradelockerNameless },
  { apiCode: 'MTR', displayName: 'MatchTrader', logo: matchTraderLogo, logoDark: matchTraderLogoDark, namelessLogo: matchTraderNameless },
  { apiCode: 'TV', displayName: 'Tradovate', logo: tradovateLogo, logoDark: tradovateLogoDark, namelessLogo: tradovateNameless },
  {
    apiCode: 'PROJECT-X',
    displayName: 'PROJECT-X',
    drawerId: 'PX',
    logo: projectXLogo,
    logoDark: projectXLogo,
    namelessLogo: projectXNameless,
    iconBg: '#000'
  },
  { apiCode: 'RM', displayName: 'Rithmic', logo: rithmicLogo, logoDark: rithmicLogo, namelessLogo: rithmicNameless },
  { apiCode: 'NT', displayName: 'NinjaTrader', logo: ninjaTraderLogo, logoDark: ninjaTraderLogo, namelessLogo: ninjaTraderNameless }
];

// Index by every spelling a caller might hold, so no one needs a private map.
const platformByKey: Record<string, PlatformMeta> = PLATFORMS.reduce((acc, platform) => {
  acc[platform.apiCode] = platform;
  acc[platform.displayName] = platform;
  if (platform.drawerId) acc[platform.drawerId] = platform;
  return acc;
}, {} as Record<string, PlatformMeta>);

/** Resolve platform metadata by API code, display name, or AddAccountDrawer id. */
export const getPlatform = (value: string): PlatformMeta | undefined => platformByKey[value];

/** Display name for an API code, falling back to the raw value when unknown. */
export const getPlatformDisplayName = (value: string): string => getPlatform(value)?.displayName ?? value;

/** Theme-aware logo for a platform, or undefined when no metadata exists. */
export const getPlatformLogo = (value: string, isDark: boolean): string | undefined => {
  const platform = getPlatform(value);
  if (!platform) return undefined;
  return isDark ? platform.logoDark : platform.logo;
};

const METATRADER_API_CODES = new Set(['MT-4', 'MT-5']);

/** Whether a platform (apiCode or displayName) is MetaTrader. */
export const isMetaTraderPlatform = (value: string): boolean => {
  const platform = getPlatform(value);
  return !!platform && METATRADER_API_CODES.has(platform.apiCode);
};
